import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Table,
  Label,
  FormGroup,
  Badge,
} from "reactstrap";
import {
  getSmsSettingsByMerchant,
  createSmsSettings,
  updateSmsSettings,
  toggleSmsSettingsActive,
  deleteSmsSettings,
} from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { useSelector } from "react-redux";
import ToggleSwitch from "../../shared/ToggleSwitch";

const SMS_PROVIDERS = ["MSEGAT", "AUTHENTICA"];

const SmsSettings = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [smsSettings, setSmsSettings] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [formData, setFormData] = useState({
    provider: "MSEGAT",
    senderId: "",
    username: "",
    apiKey: "",
    apiSecret: "",
    templateId: "",
    fallbackEmail: "",
    isTest: false,
  });

  // Delete confirm modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSmsSettings = async (merchantId) => {
    if (!merchantId) return;
    setIsFetching(true);
    const data = await getSmsSettingsByMerchant(merchantId);
    setSmsSettings(data || []);
    setIsFetching(false);
  };

  useEffect(() => {
    if (selectedMerchantId) {
      fetchSmsSettings(selectedMerchantId);
    } else {
      setSmsSettings([]);
    }
  }, [selectedMerchantId]);

  const resetForm = () => {
    setFormData({
      provider: "MSEGAT",
      senderId: "",
      username: "",
      apiKey: "",
      apiSecret: "",
      templateId: "",
      fallbackEmail: "",
      isTest: false,
    });
    setEditingSettings(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (settings) => {
    setEditingSettings(settings);
    setFormData({
      provider: settings.provider || "MSEGAT",
      senderId: settings.sender_id || "",
      username: settings.username || "",
      apiKey: "",
      apiSecret: "",
      templateId: settings.template_id || "",
      fallbackEmail: settings.fallback_email || "",
      isTest: !!settings.is_test,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.provider || !formData.senderId) {
      errorToast("Provider and Sender ID are required");
      return;
    }

    if (editingSettings) {
      const payload = { ...formData };
      if (!payload.apiKey) delete payload.apiKey;
      if (!payload.apiSecret) delete payload.apiSecret;
      const result = await updateSmsSettings(editingSettings.id, payload);
      if (result) {
        setModalOpen(false);
        fetchSmsSettings(selectedMerchantId);
      }
    } else {
      const result = await createSmsSettings({
        ...formData,
        merchantId: selectedMerchantId,
      });
      if (result) {
        setModalOpen(false);
        fetchSmsSettings(selectedMerchantId);
      }
    }
  };

  const handleToggleActive = async (id) => {
    const result = await toggleSmsSettingsActive(id);
    if (result) {
      fetchSmsSettings(selectedMerchantId);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await deleteSmsSettings(deleteTarget.id);
    if (result) {
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchSmsSettings(selectedMerchantId);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="SMS Settings" parent="Merchants" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">SMS Configurations</h5>
                  {selectedMerchantId && (
                    <Button color="primary" onClick={openCreateModal}>
                      Add SMS Settings
                    </Button>
                  )}
                </div>

                {!selectedMerchantId ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Please select a merchant from the header to view SMS settings.</p>
                  </div>
                ) : isFetching ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Loading SMS settings...</p>
                  </div>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Provider</th>
                        <th>Sender ID</th>
                        <th>Username</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsSettings.length > 0 ? (
                        smsSettings.map((settings) => (
                          <tr key={settings.id} style={{ verticalAlign: "middle" }}>
                            <td>{settings.id}</td>
                            <td>{settings.provider}</td>
                            <td>{settings.sender_id}</td>
                            <td>{settings.username || "-"}</td>
                            <td>
                              <Badge color={settings.is_active ? "success" : "secondary"}>
                                {settings.is_active ? "Active" : "Inactive"}
                              </Badge>
                              {settings.is_test && (
                                <Badge color="warning" style={{ marginLeft: 6 }} title="Test mode — SMS send failures are tolerated, customer can verify with 8899">
                                  TEST
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                                <i
                                  className="fa fa-pencil"
                                  style={{ fontSize: 18, cursor: "pointer", color: "rgb(40, 167, 69)" }}
                                  title="Edit"
                                  onClick={() => openEditModal(settings)}
                                />
                                <ToggleSwitch
                                  checked={settings.is_active}
                                  handleToggle={() => handleToggleActive(settings.id)}
                                />
                                <i
                                  className="fa fa-trash"
                                  style={{ fontSize: 18, cursor: "pointer", color: "rgb(220, 53, 69)" }}
                                  title="Delete"
                                  onClick={() => {
                                    setDeleteTarget(settings);
                                    setDeleteModal(true);
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No SMS settings configured for this merchant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingSettings ? "Edit SMS Settings" : "Add SMS Settings"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Provider *</Label>
            <Input
              type="select"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
            >
              {SMS_PROVIDERS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Sender ID *</Label>
            <Input
              type="text"
              value={formData.senderId}
              onChange={(e) => setFormData({ ...formData, senderId: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>Username</Label>
            <Input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>API Key {editingSettings && "(leave blank to keep current)"}</Label>
            <Input
              type="password"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>API Secret {editingSettings && "(leave blank to keep current)"}</Label>
            <Input
              type="password"
              value={formData.apiSecret}
              onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
            />
          </FormGroup>
          {formData.provider === "AUTHENTICA" && (
            <>
              <FormGroup>
                <Label>Template ID</Label>
                <Input
                  type="number"
                  value={formData.templateId}
                  onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Fallback Email</Label>
                <Input
                  type="email"
                  value={formData.fallbackEmail}
                  onChange={(e) => setFormData({ ...formData, fallbackEmail: e.target.value })}
                />
              </FormGroup>
            </>
          )}
          <FormGroup check style={{ marginTop: 12 }}>
            <Label check>
              <Input
                type="checkbox"
                checked={!!formData.isTest}
                onChange={(e) => setFormData({ ...formData, isTest: e.target.checked })}
              />{" "}
              <strong>Test mode</strong>
            </Label>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              When enabled, waitlist OTP send failures (provider misconfig, wrong country, etc.) are tolerated — the flow continues so the customer can verify with the <code>8899</code> bypass code. Keep this off in production.
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {editingSettings ? "Update" : "Create"}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete SMS settings for provider{" "}
          <strong>{deleteTarget?.provider}</strong> (ID: {deleteTarget?.id})?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default SmsSettings;
